import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Header from "src/components/wizard/Header";
import Footer from "src/components/wizard/Footer/Footer";
import { fullWidthDialog } from "src/constants/dialog";
import {
  MOVEWARE_ACTIVITIES_QUERY,
  PROJECT_INTEGRATION_RECORDS_QUERY,
} from "src/constants/graphql/queries";
import MovewareJobConfirmDetails from "./MovewareJobConfirmDetails";
import {
  dockActionName,
  formatDate,
  matchesDockActivityType,
  optionFromActivity,
  shiftIsoDate,
  toIsoDate,
} from "./movewareActivityOptions";

const locateRange = date => ({
  dateFrom: shiftIsoDate(date, -7),
  dateTo: shiftIsoDate(date, 7),
});

const dialogSx = onConfirmStep => ({
  "& .MuiDialog-paper": {
    ...fullWidthDialog["& .MuiDialog-paper"],
    height: onConfirmStep ? "auto" : 480,
    maxHeight: "calc(100% - 48px)",
  },
});

const activityListSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
};

function ActivityList({ options, selected, onSelect }) {
  return (
    <List disablePadding sx={activityListSx}>
      {options.map(option => (
        <ListItemButton
          key={option.value}
          selected={selected?.value === option.value}
          divider
          onClick={() => onSelect(option)}
        >
          <ListItemText
            primary={`Job ${option.jobId}`}
            secondary={[option.description, option.contact, option.date].filter(Boolean).join(" · ")}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
          <ChevronRightIcon color="action" />
        </ListItemButton>
      ))}
    </List>
  );
}

export default function MovewareDiaryActionModal({
  open,
  embedded = false,
  projectId,
  date,
  activityType,
  linkedOption,
  confirming,
  removing,
  onClose,
  onConfirm,
  onRemove,
  onLocateState,
}) {
  const [step, setStep] = useState(linkedOption ? "current" : "locate");
  const [mode, setMode] = useState("list");
  const [searchInput, setSearchInput] = useState("");
  const [searchId, setSearchId] = useState("");
  const [selected, setSelected] = useState(linkedOption || null);
  const [confirmStatus, setConfirmStatus] = useState({ loading: false, error: null });
  const [othersOpen, setOthersOpen] = useState(false);
  const [lookupByChoice, setLookupByChoice] = useState(false);
  const viewingCurrent = step === "current";
  const viewingConfirm = step === "confirm" || viewingCurrent;
  const range = locateRange(date);
  const todayIso = toIsoDate(date);

  const active = open || embedded;

  useEffect(() => {
    if (!active) return;

    setStep(linkedOption ? "current" : "locate");
    setMode("list");
    setSearchInput("");
    setSearchId("");
    setSelected(linkedOption || null);
    setConfirmStatus({ loading: false, error: null });
    setOthersOpen(false);
    setLookupByChoice(false);
  }, [active, date, linkedOption?.jobId]);

  const { data: projectData } = useQuery(PROJECT_INTEGRATION_RECORDS_QUERY, {
    variables: { id: projectId },
    skip: !active || !projectId,
  });
  const projectRecord = projectData?.project?.externalRecords?.find(
    record => record.recordType === "storage_account"
  );
  const customerName = projectRecord?.data?.customer_name || "this customer";

  const lookingUp = mode === "lookup";
  const lookupReady = lookingUp && Boolean(searchId) && searchInput.trim() === searchId;
  const skipActivities = !active || !projectId || step !== "locate" || (lookingUp && !lookupReady);

  const { data, loading, error } = useQuery(MOVEWARE_ACTIVITIES_QUERY, {
    variables: {
      projectId,
      date,
      ...(activityType ? { activityType } : {}),
      ...(lookupReady ? { searchId } : range),
    },
    skip: skipActivities,
    fetchPolicy: "network-only",
  });

  const options = useMemo(
    () =>
      [...(data?.movewareActivities?.activities || [])]
        .filter(activity => !activityType || matchesDockActivityType(activity.type, activityType))
        .filter(activity => lookingUp || activity.date)
        .map(optionFromActivity)
        .filter(Boolean)
        .sort((left, right) => {
          const byDate = (right.isoDate || "").localeCompare(left.isoDate || "");
          if (byDate) return byDate;
          return (right.time || "").localeCompare(left.time || "");
        }),
    [activityType, data, lookingUp]
  );
  const todayOptions = options.filter(option => option.isoDate === todayIso);
  const daysFromAnchor = iso =>
    iso ? Math.abs(Date.parse(`${iso}T00:00:00`) - Date.parse(`${todayIso}T00:00:00`)) : Number.POSITIVE_INFINITY;
  const otherOptions = options
    .filter(option => option.isoDate !== todayIso)
    .sort((left, right) => {
      const byDistance = daysFromAnchor(left.isoDate) - daysFromAnchor(right.isoDate);
      if (byDistance) return byDistance;
      return (right.isoDate || "").localeCompare(left.isoDate || "");
    });
  const anchorDateLabel = formatDate(todayIso);
  const actionName = dockActionName(activityType);
  const actionPhrase = activityType ? `${actionName} actions` : "diary actions";

  const waiting = loading;
  const showResults = !lookingUp || lookupReady;
  const emptyList = showResults && !waiting && !error && options.length === 0;

  useEffect(() => {
    if (lookingUp || waiting || error || step !== "locate") return;
    if (!data) return;
    if (options.length === 0) setMode("lookup");
  }, [data, error, lookingUp, options.length, step, waiting]);

  const openJobSearch = () => {
    setLookupByChoice(true);
    setMode("lookup");
    setSearchInput("");
    setSearchId("");
    setSelected(null);
  };

  const backToList = () => {
    setLookupByChoice(false);
    setMode("list");
    setSearchInput("");
    setSearchId("");
    setSelected(null);
  };
  const view =
    step === "locate" && lookingUp ? "lookup" : step === "locate" ? "list" : viewingCurrent ? "current" : "confirm";
  const confirmDisabled =
    confirmStatus.loading || !!confirmStatus.error || confirming || !selected?.jobId;
  const chromeRef = useRef({});
  chromeRef.current = {
    openJobSearch,
    backToList,
    backFromConfirm: () => setStep(linkedOption ? "current" : "locate"),
    change: () => setStep("locate"),
  };

  useEffect(() => {
    onLocateState?.({
      view,
      lookupByChoice,
      waiting,
      emptyList,
      selected,
      confirmDisabled,
      openJobSearch: () => chromeRef.current.openJobSearch(),
      backToList: () => chromeRef.current.backToList(),
      backFromConfirm: () => chromeRef.current.backFromConfirm(),
      change: () => chromeRef.current.change(),
    });
  }, [confirmDisabled, emptyList, lookupByChoice, onLocateState, selected, view, waiting]);

  const findJob = () => {
    const value = searchInput.trim();
    if (!value) return;

    setSearchId(value);
    setSelected(null);
  };

  const handleSelect = option => {
    setSelected(option);
    setStep("confirm");
  };

  const body = step === "locate" ? (
    <Box>
      {lookingUp ? (
        <Box>
          {!lookupByChoice && (
            <Typography sx={{ mb: 1.5 }}>
              {`No ${actionPhrase} were found for ${customerName} in this date range.`}
            </Typography>
          )}
          <Typography sx={{ mb: 1.5 }}>Input Moveware Job ID</Typography>
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={searchInput}
              onChange={event => {
                setSearchInput(event.target.value);
                setSelected(null);
              }}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  findJob();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={findJob}
              disabled={!searchInput.trim()}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Find
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography sx={{ mb: 2 }}>
          {`${waiting ? "Searching" : "Showing"} for ${actionPhrase} for ${customerName} on ${anchorDateLabel}`}
        </Typography>
      )}

      {error && showResults && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {waiting && showResults && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!waiting && !error && emptyList && lookupReady && (
        <Typography color="error" sx={{ fontWeight: 500, mb: 1 }}>
          No {activityType ? `${actionName} action` : "diary action"} was found for that job ID.
        </Typography>
      )}

      {showResults && !waiting && !error && options.length > 0 && (
        <Box>
          {todayOptions.length > 0 ? (
            <ActivityList options={todayOptions} selected={selected} onSelect={handleSelect} />
          ) : (
            !lookingUp && (
              <Typography color="text.secondary" sx={{ mb: otherOptions.length ? 1.5 : 0 }}>
                {`No ${actionName || "diary"} actions on ${anchorDateLabel}`}
              </Typography>
            )
          )}
          {otherOptions.length > 0 && (
            <Box sx={{ mt: todayOptions.length > 0 ? 1.5 : 0 }}>
              {todayOptions.length > 0 ? (
                <>
                  <Button
                    onClick={() => setOthersOpen(open => !open)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: othersOpen ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s",
                        }}
                      />
                    }
                    sx={{
                      textTransform: "none",
                      px: 0,
                      minHeight: 32,
                      mb: othersOpen ? 1 : 0,
                      fontSize: "1rem",
                    }}
                  >
                    {`other dates (${otherOptions.length})`}
                  </Button>
                  <Collapse in={othersOpen} timeout="auto" unmountOnExit>
                    <ActivityList
                      options={otherOptions}
                      selected={selected}
                      onSelect={handleSelect}
                    />
                  </Collapse>
                </>
              ) : (
                <ActivityList options={otherOptions} selected={selected} onSelect={handleSelect} />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : (
    <Box>
      {viewingCurrent && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1.5 }}
        >
          <Typography>Currently linked</Typography>
          {embedded && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                color="error"
                onClick={onRemove}
                disabled={removing || confirming}
                sx={{ textTransform: "none" }}
              >
                Remove
              </Button>
              <Button
                variant="outlined"
                onClick={() => setStep("locate")}
                disabled={removing || confirming}
                sx={{ textTransform: "none" }}
              >
                Change
              </Button>
            </Box>
          )}
        </Box>
      )}
      <MovewareJobConfirmDetails
        jobId={selected?.jobId || linkedOption?.jobId}
        option={selected || linkedOption}
        projectId={projectId}
        activityType={activityType || selected?.type || linkedOption?.type}
        onStatus={setConfirmStatus}
      />
    </Box>
  );

  const actions =
    step === "locate" ? (
      lookingUp && lookupByChoice ? (
        embedded ? null : (
          <Button variant="outlined" onClick={backToList}>
            Back
          </Button>
        )
      ) : lookingUp ? null : (
        <Button
          variant={emptyList ? "contained" : "outlined"}
          onClick={openJobSearch}
          disabled={waiting}
          sx={{ textTransform: "none" }}
        >
          Search By Job ID
        </Button>
      )
    ) : viewingCurrent ? (
      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          color="error"
          onClick={onRemove}
          disabled={removing || confirming}
          sx={{ textTransform: "none" }}
        >
          Remove
        </Button>
        <Button
          variant="outlined"
          onClick={() => setStep("locate")}
          disabled={removing || confirming}
          sx={{ textTransform: "none" }}
        >
          Change
        </Button>
      </Box>
    ) : (
      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          onClick={() => setStep(linkedOption ? "current" : "locate")}
          disabled={confirming}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={
            confirmStatus.loading || !!confirmStatus.error || confirming || !selected?.jobId
          }
          onClick={() => onConfirm(selected)}
        >
          Use This Job
        </Button>
      </Box>
    );

  if (embedded) return body;

  return (
    <Dialog open={open} maxWidth="xs" fullWidth onClose={onClose} sx={dialogSx(viewingConfirm)}>
      <Box
        display="flex"
        flexDirection="column"
        height={viewingConfirm ? "auto" : "100%"}
        maxHeight="100%"
      >
        <Header title="Link Moveware Diary Action" onClose={onClose} />
        <DialogContent
          sx={{
            px: 3,
            py: viewingConfirm ? 1.5 : 2,
            flex: viewingConfirm ? "0 1 auto" : 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {body}
        </DialogContent>
        <Footer step={step === "locate" ? 0 : 1} steps={2} showRaceMode={false}>
          {actions}
        </Footer>
      </Box>
    </Dialog>
  );
}
